import React, { useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { FaInstagram, FaFacebookF, FaWhatsapp } from "react-icons/fa";

const ContactSection = () => {
  const container = useRef();

  useGSAP(
    () => {
      gsap.from(".contact-fade", {
        opacity: 0,
        y: 20,
        duration: 1,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 80%",
        },
      });
    },
    { scope: container },
  );

  return (
    <SectionWrapper ref={container}>
      <ContentLayout>
        {/* LEFT: ESSENTIALS */}
        <InfoSide className="contact-fade">
          <Label>Connect</Label>
          <MainTitle>
            Fine-tune <br />
            your beauty.
          </MainTitle>

          <ContactDetails>
            <DetailLink href="tel:+1234567890">+1 (234) 567-890</DetailLink>
            <DetailLink href="mailto:hello@clinic.com">
              hello@aestheticstudio.com
            </DetailLink>
          </ContactDetails>

          <SocialRow>
            <IconLink href="#">
              <FaInstagram />
            </IconLink>
            <IconLink href="#">
              <FaFacebookF />
            </IconLink>
            <IconLink href="#">
              <FaWhatsapp />
            </IconLink>
          </SocialRow>
        </InfoSide>

        {/* RIGHT: MINIMAL FORM */}
        <FormSide>
          <InputGroup className="contact-fade">
            <input type="text" placeholder="Name" />
          </InputGroup>
          <InputGroup className="contact-fade">
            <input type="email" placeholder="Email" />
          </InputGroup>
          <InputGroup className="contact-fade">
            <textarea placeholder="Message" rows="1" />
          </InputGroup>

          <SubmitArea className="contact-fade">
            <button type="submit">Submit Request</button>
          </SubmitArea>
        </FormSide>
      </ContentLayout>

      <BottomLegal className="contact-fade">
        © 2026 AESTHETIC STUDIO — ALL RIGHTS RESERVED
      </BottomLegal>
    </SectionWrapper>
  );
};

export default ContactSection;

// --- Styled Components ---

const MAIN_COLOR = "#5a5248";

const SectionWrapper = styled.section`
  width: 100%;
  padding: 100px 5%;
  background: transparent;
  color: ${MAIN_COLOR};
  display: flex;
  flex-direction: column;
  align-items: center; /* Centers the whole layout */
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  /* Reduced gap and max-width brings both sides closer together */
  gap: 60px;
  width: 100%;
  max-width: 900px; /* This keeps the sides from drifting apart on large screens */

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 50px;
    max-width: 500px;
  }
`;

const InfoSide = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.span`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.3rem;
  margin-bottom: 1rem;
  opacity: 0.8;
`;

const MainTitle = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 400;
  line-height: 1.2;
  margin-bottom: 2.5rem;
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 30px;
`;

const DetailLink = styled.a`
  font-size: 1rem;
  text-decoration: none;
  color: ${MAIN_COLOR};
  width: fit-content;
  opacity: 0.9;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 18px;
`;

const IconLink = styled.a`
  color: ${MAIN_COLOR};
  font-size: 1.1rem;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.5;
  }
`;

const FormSide = styled.form`
  display: flex;
  flex-direction: column;
  gap: 30px; /* Slightly tighter spacing for form fields */
  padding-top: 10px;
`;

const InputGroup = styled.div`
  border-bottom: 1px solid rgba(90, 82, 72, 0.2);
  padding-bottom: 8px;

  input,
  textarea {
    width: 100%;
    background: transparent;
    border: none;
    color: ${MAIN_COLOR};
    font-size: 1rem;
    outline: none;
    font-family: inherit;

    &::placeholder {
      color: rgba(90, 82, 72, 0.4);
    }
  }

  &:focus-within {
    border-color: ${MAIN_COLOR};
  }
`;

const SubmitArea = styled.div`
  margin-top: 10px;

  button {
    background: ${MAIN_COLOR};
    color: #fff;
    border: none;
    padding: 14px 35px;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.15rem;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #4a433a;
    }
  }
`;

const BottomLegal = styled.div`
  margin-top: 80px;
  font-size: 0.6rem;
  letter-spacing: 0.15rem;
  opacity: 0.4;
`;
